import { SKILL_CATEGORIES, SKILL_CATEGORY_LABELS, type SkillCategory } from '@remote-medium/shared';
import { errorMessage, unwrap, useApi } from '@remote-medium/ui';
import { useSearchParams } from 'react-router';
import { useState } from 'react';

import { admin } from '../../lib/client.ts';
import { Notice, Page, PageHeader, Tabs } from '../../lib/ui.tsx';
import { TaxonomySection } from './components/TaxonomySection.tsx';

type Tab = 'skills' | 'roleTypes' | 'industries';
const CATEGORY_OPTIONS = SKILL_CATEGORIES.map((c) => ({
  value: c,
  label: SKILL_CATEGORY_LABELS[c],
}));

export function TaxonomyPage() {
  const { data, error, loading, reload } = useApi(() => unwrap(admin.taxonomy.$get()), []);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'skills';
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  // Resolves true on success so the section can clear its inline form.
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setMsg(null);
    try {
      await fn();
      setMsg({ tone: 'info', text: ok });
      reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    }
  };

  // Pending skills are reviewed on the Suggestions page, not edited here.
  const skills = (data?.skills ?? []).filter((s) => s.status === 'approved');
  const roleTypes = data?.roleTypes ?? [];
  const industries = data?.industries ?? [];
  const status = { loading, error };
  const families = [
    ...new Set(roleTypes.filter((r) => r.status === 'approved').map((r) => r.family)),
  ].map((f) => ({ value: f, label: f }));
  const roleTypeOptions = roleTypes
    .filter((r) => r.status === 'approved')
    .map((r) => ({ value: r.id, label: `${r.family} · ${r.name}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const industryOptions = industries.map((i) => ({ value: i.id, label: i.name }));
  const industriesSaved = (ids: string[]) =>
    ids.length
      ? `Set industries: ${ids.map((id) => industries.find((i) => i.id === id)?.name ?? id).join(', ')}.`
      : 'Marked for any industry.';

  return (
    <Page className="gap-6">
      <PageHeader
        title="Taxonomy"
        description="The shared vocabulary for candidates and roles. Ranking matches on these entries, so keep them tidy. Renames keep every link; a merge moves links to the surviving entry and deletes the duplicate."
      />

      <Tabs
        label="Taxonomy list"
        value={tab}
        onChange={(t) => {
          setMsg(null);
          setParams(t === 'skills' ? {} : { tab: t }, { replace: true });
        }}
        tabs={[
          { value: 'skills', label: 'Skills', count: skills.length },
          {
            value: 'roleTypes',
            label: 'Role types',
            count: data?.roleTypes.filter((r) => r.status === 'approved').length,
          },
          { value: 'industries', label: 'Industries', count: data?.industries.length },
        ]}
      />

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {tab === 'skills' && (
        <TaxonomySection
          key="skills"
          noun="skill"
          {...status}
          items={skills.map((s) => ({
            id: s.id,
            label: s.name,
            extra: s.category ?? '',
            tags: { families: s.families, industries: s.industries, roleTypes: s.roleTypes },
          }))}
          onCreate={(name, extra) =>
            act(
              () =>
                unwrap(
                  admin.taxonomy.skills.$post({ json: { name, category: extra as SkillCategory } }),
                ),
              `Added ${name}.`,
            )
          }
          onRename={(id, name) =>
            act(
              () => unwrap(admin.taxonomy.skills[':id'].$patch({ param: { id }, json: { name } })),
              `Renamed to ${name}.`,
            )
          }
          onMerge={(id, intoId) =>
            act(
              () =>
                unwrap(
                  admin.taxonomy.skills[':id'].merge.$post({ param: { id }, json: { intoId } }),
                ),
              'Merged.',
            )
          }
          extraOptions={CATEGORY_OPTIONS}
          extraLabel="Category"
          tagSets={[
            {
              key: 'families',
              heading: 'For role families',
              button: 'Families',
              emptyText: 'Generic',
              options: families,
              onSave: (id, next) =>
                act(
                  () =>
                    unwrap(
                      admin.taxonomy.skills[':id'].$patch({
                        param: { id },
                        json: { families: next },
                      }),
                    ),
                  next.length ? `Set families: ${next.join(', ')}.` : 'Marked generic.',
                ),
            },
            {
              key: 'industries',
              heading: 'For industries',
              button: 'Industries',
              emptyText: 'Any',
              options: industryOptions,
              onSave: (id, next) =>
                act(
                  () =>
                    unwrap(
                      admin.taxonomy.skills[':id'].$patch({
                        param: { id },
                        json: { industries: next },
                      }),
                    ),
                  industriesSaved(next),
                ),
            },
            {
              key: 'roleTypes',
              heading: 'For role types',
              button: 'Role types',
              emptyText: 'Any',
              options: roleTypeOptions,
              searchable: true,
              onSave: (id, next) =>
                act(
                  () =>
                    unwrap(
                      admin.taxonomy.skills[':id'].$patch({
                        param: { id },
                        json: { roleTypes: next },
                      }),
                    ),
                  next.length ? `Set role types (${next.length}).` : 'Not tied to a role type.',
                ),
            },
          ]}
        />
      )}
      {tab === 'roleTypes' && (
        <TaxonomySection
          // Remount once loaded so the add form defaults to the first family.
          key={data ? 'roleTypes' : 'roleTypes-loading'}
          noun="role type"
          {...status}
          // Pending types are reviewed on the Suggestions page, not edited here.
          items={roleTypes
            .filter((r) => r.status === 'approved')
            .map((r) => ({
              id: r.id,
              label: r.name,
              extra: r.family,
              tags: { industries: r.industries },
            }))}
          onCreate={(name, extra) =>
            act(
              () => unwrap(admin.taxonomy['role-types'].$post({ json: { family: extra, name } })),
              `Added ${extra} · ${name}.`,
            )
          }
          onRename={(id, name) =>
            act(
              () =>
                unwrap(
                  admin.taxonomy['role-types'][':id'].$patch({ param: { id }, json: { name } }),
                ),
              `Renamed to ${name}.`,
            )
          }
          onMerge={(id, intoId) =>
            act(
              () =>
                unwrap(
                  admin.taxonomy['role-types'][':id'].merge.$post({
                    param: { id },
                    json: { intoId },
                  }),
                ),
              'Merged.',
            )
          }
          extraOptions={families}
          extraLabel="Family"
          allowNewExtra
          tagSets={[
            {
              key: 'industries',
              heading: 'For industries',
              button: 'Industries',
              emptyText: 'Any',
              options: industryOptions,
              onSave: (id, next) =>
                act(
                  () =>
                    unwrap(
                      admin.taxonomy['role-types'][':id'].$patch({
                        param: { id },
                        json: { industries: next },
                      }),
                    ),
                  industriesSaved(next),
                ),
            },
          ]}
        />
      )}
      {tab === 'industries' && (
        <TaxonomySection
          key="industries"
          noun="industry"
          {...status}
          items={industries.map((i) => ({ id: i.id, label: i.name, extra: '' }))}
          onCreate={(name) =>
            act(() => unwrap(admin.taxonomy.industries.$post({ json: { name } })), `Added ${name}.`)
          }
          onRename={(id, name) =>
            act(
              () =>
                unwrap(admin.taxonomy.industries[':id'].$patch({ param: { id }, json: { name } })),
              `Renamed to ${name}.`,
            )
          }
          onMerge={(id, intoId) =>
            act(
              () =>
                unwrap(
                  admin.taxonomy.industries[':id'].merge.$post({
                    param: { id },
                    json: { intoId },
                  }),
                ),
              'Merged.',
            )
          }
        />
      )}
    </Page>
  );
}
